import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useContacts from "../../../services/chat/useContacts";
import ContactAdd from "./ContactAdd";
import { ToastContainer } from "react-toastify";

const ContactBook = ({ userID }) => {
  const navigate = useNavigate();

  const { contacts, audience, loading, getContacts } = useContacts(userID);

  const [selectedAudienceGroup, setSelectedAudienceGroup] = useState(null);
  const [filter, setFilter] = useState("contactbook");
  const [searchTerm, setSearchTerm] = useState("");

  const filterData = (data = []) => {
    return data.filter((item) => {
      const name = item.name || item.contact_name || "";
      const number = item.mobile || item.contact_number || item.contactNumber || "";

      return (
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(number).toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  };

  const sortLatestFirst = (data = []) => {
    return [...data].sort((a, b) => {
      const dateA = new Date(
        a.created_at || a.createdAt || a.indate || a.updated_at || 0
      );
      const dateB = new Date(
        b.created_at || b.createdAt || b.indate || b.updated_at || 0
      );

      // If date exists, latest first
      if (!isNaN(dateA) && !isNaN(dateB) && dateA - dateB !== 0) {
        return dateB - dateA;
      }

      // Fallback: higher id first
      return Number(b.id || 0) - Number(a.id || 0);
    });
  };

  const displayedContacts = selectedAudienceGroup
    ? filterData(selectedAudienceGroup.members)
    : filterData(sortLatestFirst(contacts));

  const handleContactAdded = async () => {
    await getContacts?.(userID);
    setSelectedAudienceGroup(null);
    setSearchTerm("");
    setFilter("contactbook");
  };

  const handleOpenChat = (item) => {
    const contactName = item.name || item.contact_name || "N/A";
    const contactNumber =
      item.mobile || item.contact_number || item.contactNumber || "";

    if (!contactNumber) return;

    navigate("/Chat", {
      state: {
        contact: item,
        contactName,
        contactNumber,
      },
    });
  };

  return (
    <>
    <ToastContainer/>

    <div className="p-6 min-h-screen montserrat-font-1">
      {filter === "addnew" && (
        <>
          <button
            onClick={() => setFilter("contactbook")}
            className="bg-blue-500 text-white px-4 py-2 rounded mb-4"
          >
            Contact Book
          </button>

          <ContactAdd userID={userID} onContactAdded={handleContactAdded} />
        </>
      )}

      {filter === "contactbook" && (
        <>
          {/* Header */}
          <div className="flex justify-between items-center mb-6 montserrat-font-1">
            <h2 className="text-xl font-semibold">
              {selectedAudienceGroup
                ? `Members of ${selectedAudienceGroup.audience_group_name}`
                : "Contact Book"}
            </h2>

            <div className="flex gap-3 montserrat-font-1">
              {/* Audience Dropdown */}
              <select
                className="border rounded px-3 py-2"
                onChange={(e) => {
                  const selectedId = e.target.value;

                  if (!selectedId) {
                    setSelectedAudienceGroup(null);
                    return;
                  }

                  const selectedGroup = audience.find(
                    (group) => String(group.id) === String(selectedId)
                  );

                  setSelectedAudienceGroup(selectedGroup || null);
                }}
              >
                <option value="">All Contact List</option>

                {audience.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.audience_group_name}
                  </option>
                ))}
              </select>

              {/* Search */}
              <input
                type="text"
                placeholder="Search..."
                className="border rounded px-3 py-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {/* Add Button */}
              <button
                onClick={() => setFilter("addnew")}
                className="bg-green-500 text-white px-4 py-2 rounded"
              >
                Add Contact
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden montserrat-font-1">
            <table className="w-full text-center">
              <thead className="bg-green-500 text-white">
                <tr>
                  <th className="p-3">S.No</th>
                  <th className="p-3">Contact Name</th>
                  <th className="p-3">Contact Number</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" className="p-6">
                      Loading...
                    </td>
                  </tr>
                ) : displayedContacts.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-6 text-gray-500">
                      No contacts found
                    </td>
                  </tr>
                ) : (
                  displayedContacts.map((item, index) => {
                    const contactName = item.name || item.contact_name || "N/A";
                    const contactNumber =
                      item.mobile ||
                      item.contact_number ||
                      item.contactNumber ||
                      "N/A";

                    return (
                      <tr key={item.id || index} className="border-b">
                        <td className="p-3">{index + 1}</td>

                        <td className="p-3">{contactName}</td>

                        <td className="p-3">{contactNumber}</td>

                        <td className="p-3">
                          <div className="flex justify-center gap-4">
                            <button
                              onClick={() => handleOpenChat(item)}
                              className="text-green-600 hover:underline font-medium"
                            >
                              Chat
                            </button>

                            <button className="text-blue-500 hover:underline">
                              Edit
                            </button>

                            <button className="text-red-500 hover:underline">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
    </>
  );
};

export default ContactBook;